/**
 * @file shipment-state.ts
 * @description Производные состояния отгрузки (phase19.12 шаг 6).
 *
 * Главный вопрос карточки: отгрузка уже у перевозчика или ещё черновик. От
 * этого зависит всё — можно ли менять тариф и места, показывать ли кнопку
 * передачи и доступны ли печатные формы.
 *
 * Второй вопрос (phase19.13 шаг 6) — не зависла ли отгрузка. Правила и их
 * приоритет повторяют серверные (`shipment-attention-rules.ts`): бейдж на
 * карточке и строка в списке «Требуют внимания» обязаны говорить одно и то же.
 */

import type { ShipmentDto } from "./api-orders";
import type { DeliveryStage, ShipmentAttentionRule } from "./api-delivery-monitoring";

/**
 * Отгрузка уже передана перевозчику.
 *
 * Основной признак — `externalCarrierUuid` (заявка создана в СДЭК). Трек и
 * статус проверяем дополнительно: заявка могла быть создана раньше, чем поле
 * появилось в ответе, а показывать кнопку «Передать» повторно нельзя —
 * сервер ответит 422 «Отгрузка уже передана в СДЭК».
 */
export function isShipmentHandedToCarrier(shipment: ShipmentDto): boolean {
  return (
    Boolean(shipment.externalCarrierUuid) ||
    Boolean(shipment.trackNumber) ||
    shipment.status !== "PENDING"
  );
}

/** Черновик: тариф и места ещё можно менять, заявку — создать. */
export function isShipmentDraft(shipment: ShipmentDto): boolean {
  return !isShipmentHandedToCarrier(shipment);
}

/**
 * Трек-номер для показа. Сразу после передачи его нет: СДЭК обрабатывает
 * заявку асинхронно и может её отклонить — поэтому пишем, что происходит,
 * а не просто «трека нет».
 */
export function shipmentTrackLabel(shipment: ShipmentDto): string {
  return shipment.trackNumber ?? "СДЭК обрабатывает (номер ещё не присвоен)";
}

// ─── Зависшие отправления (phase19.13 шаг 6) ────────────────────────────────

/** Пороги мониторинга магазина. */
export interface DeliveryStaleThresholds {
  staleTrackTimeoutHours: number;
  staleNoMovementDays: number;
  stalePickupWaitDays: number;
}

/**
 * Дефолты серверной схемы. Нужны, пока настройки магазина не загрузились:
 * карточка не должна ждать отдельный запрос, чтобы нарисовать бейдж.
 */
export const DEFAULT_STALE_THRESHOLDS: DeliveryStaleThresholds = {
  staleTrackTimeoutHours: 4,
  staleNoMovementDays: 3,
  stalePickupWaitDays: 5,
};

/** Последнее событие ленты в объёме, который нужен правилам. */
export interface ShipmentLastEvent {
  stage: DeliveryStage;
  occurredAt: string;
}

/** Пометка «зависла» на карточке отгрузки. */
export interface ShipmentStalenessBadge {
  rule: ShipmentAttentionRule;
  label: string;
  tone: "warning" | "destructive";
  /** Часов с точки отсчёта — для подсказки и отладки. */
  hoursElapsed: number;
}

const HOUR_MS = 60 * 60 * 1000;

/** Полных часов между моментами; отрицательные схлопываем в 0. */
function hoursBetween(from: Date, to: Date): number {
  const diff = to.getTime() - from.getTime();
  return diff <= 0 ? 0 : Math.floor(diff / HOUR_MS);
}

/** Часы → «6 ч» / «4 дн» (та же логика, что в списке «Требуют внимания»). */
function elapsedLabel(hours: number): string {
  if (hours < 24) return `${Math.max(1, Math.round(hours))} ч`;
  return `${Math.max(1, Math.round(hours / 24))} дн`;
}

/**
 * Отгрузка зависла? `null` — всё в порядке.
 *
 * Возвращаем ОДНУ пометку, а не список: правила упорядочены по серьёзности,
 * и менеджеру нужен ответ «что здесь не так», а не пять бейджей в ряд (тот же
 * инвариант, что на сервере).
 *
 * Точка отсчёта: `occurredAt` последнего события, без событий — `shippedAt`
 * («отгружено»), иначе `createdAt`.
 */
export function shipmentStaleness(
  shipment: ShipmentDto,
  lastEvent: ShipmentLastEvent | null,
  thresholds: DeliveryStaleThresholds = DEFAULT_STALE_THRESHOLDS,
  now: Date = new Date(),
): ShipmentStalenessBadge | null {
  const createdAt = new Date(shipment.createdAt);
  const shippedAt = shipment.shippedAt ? new Date(shipment.shippedAt) : null;
  const lastEventAt = lastEvent ? new Date(lastEvent.occurredAt) : null;

  // «Не вручено» действует и для завершённой отгрузки: перевозчик переводит
  // такую в возврат, а разбираться с клиентом и деньгами всё равно надо.
  if (lastEvent?.stage === "NOT_DELIVERED") {
    const hours = hoursBetween(lastEventAt ?? createdAt, now);
    return {
      rule: "not_delivered",
      label: "Вручение не удалось",
      tone: "destructive",
      hoursElapsed: hours,
    };
  }

  // Доставленную и возвращённую теребить бессмысленно.
  if (shipment.status === "DELIVERED" || shipment.status === "RETURNED") return null;

  if (lastEvent?.stage === "READY_FOR_PICKUP") {
    const hours = hoursBetween(lastEventAt ?? createdAt, now);
    if (hours > thresholds.stalePickupWaitDays * 24) {
      return {
        rule: "pickup_wait",
        label: `В ПВЗ ${elapsedLabel(hours)} — риск возврата`,
        tone: "destructive",
        hoursElapsed: hours,
      };
    }
  }

  // «Передана перевозчику» здесь — ровно как на сервере: заявка есть в его ИС
  // либо менеджер сам отметил отгрузку ушедшей. Расширенный
  // `isShipmentHandedToCarrier` (он учитывает ещё трек и статус) дал бы другой
  // ответ, и бейдж разошёлся бы со списком «Требуют внимания».
  const handedToCarrier =
    shipment.externalCarrierUuid !== null || shipment.shippedAt !== null;
  if (handedToCarrier && shipment.trackNumber === null) {
    const hours = hoursBetween(shippedAt ?? createdAt, now);
    if (hours > thresholds.staleTrackTimeoutHours) {
      return {
        rule: "track_not_assigned",
        label: `Трек не присвоен ${elapsedLabel(hours)}`,
        tone: "warning",
        hoursElapsed: hours,
      };
    }
  }

  const hours = hoursBetween(lastEventAt ?? shippedAt ?? createdAt, now);
  if (hours > thresholds.staleNoMovementDays * 24) {
    return {
      rule: "no_movement",
      label: `Без движения ${elapsedLabel(hours)}`,
      tone: "warning",
      hoursElapsed: hours,
    };
  }

  return null;
}
